import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Timeoff } from '../types/entities';

interface TimeoffsTableProps {
  timeoffs: Timeoff[];
  onEdit: (timeoff: Timeoff) => void;
  onDelete: (timeoff: Timeoff) => void;
}

const TimeoffsTable: React.FC<TimeoffsTableProps> = ({
  timeoffs,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const formatDateTime = (date: Date) => {
    return new Date(date).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const calculateDuration = (startDate: Date, endDate: Date) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffHours < 24) {
      return { hours: diffHours, isPartial: true, days: undefined };
    } else {
      return { days: diffDays, isPartial: false, hours: undefined };
    }
  };

  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {timeoffs.map((timeoff) => (
          <Paper key={timeoff.id} sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="h6" component="div">
                {timeoff.hairdresser?.name || t('common.notAvailable')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  color="primary"
                  onClick={() => onEdit(timeoff)}
                  size="small"
                  sx={{ padding: '4px' }}
                >
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() => onDelete(timeoff)}
                  size="small"
                  sx={{ padding: '4px' }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {formatDateTime(timeoff.startDate)} - {formatDateTime(timeoff.endDate)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {(() => {
                const duration = calculateDuration(timeoff.startDate, timeoff.endDate);
                if (duration.isPartial) {
                  return (
                    <Chip
                      label={`${duration.hours} ${t('timeoffs.hours')}`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  );
                } else {
                  return (
                    <Chip
                      label={`${duration.days} ${(duration.days || 0) === 1 ? t('timeoffs.day') : t('timeoffs.days')}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  );
                }
              })()}
            </Box>
            {timeoff.description && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {timeoff.description}
              </Typography>
            )}
          </Paper>
        ))}
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('common.hairdresser')}</TableCell>
            <TableCell>{t('timeoffs.startDate')}</TableCell>
            <TableCell>{t('timeoffs.endDate')}</TableCell>
            <TableCell>{t('timeoffs.duration')}</TableCell>
            <TableCell>{t('common.description')}</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {timeoffs.map((timeoff) => (
            <TableRow key={timeoff.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {timeoff.hairdresser?.name || t('common.notAvailable')}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDateTime(timeoff.startDate)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDateTime(timeoff.endDate)}
                </Typography>
              </TableCell>
              <TableCell>
                {(() => {
                  const duration = calculateDuration(timeoff.startDate, timeoff.endDate);
                  if (duration.isPartial) {
                    return (
                      <Chip
                        label={`${duration.hours} ${t('timeoffs.hours')}`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    );
                  } else {
                    return (
                      <Chip
                        label={`${duration.days} ${(duration.days || 0) === 1 ? t('timeoffs.day') : t('timeoffs.days')}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    );
                  }
                })()}
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {timeoff.description || t('timeoffs.noDescription')}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <IconButton
                    size="small"
                    onClick={() => onEdit(timeoff)}
                    color="primary"
                    title={t('common.edit')}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onDelete(timeoff)}
                    color="error"
                    title={t('common.delete')}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TimeoffsTable;
